'use client'

import { useEffect, useRef, useState } from 'react'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { EditorBodyFields } from '@/deskUiSkeletons/editor/EditorBodyFields'
import { contract } from '@/deskUiSkeletons/editor/Editor.helpers'
import { EditorHeadFields, HeadShort } from '@/deskUiSkeletons/editor/components/Head/Head'
import { EditorConfigureModal } from '@/deskUiSkeletons/editor/components/Modal/ConfigureModal'
import { EditorAttachmentsModal, editorAttachmentCount } from '@/deskUiSkeletons/editor/components/Modal/AttachmentsModal'
import { EditorAttachmentsStrip } from '@/deskUiSkeletons/editor/components/Attachments/AttachmentsStrip'
import { useEditorAttachments } from '@/deskUiSkeletons/editor/useEditorAttachments'
import { EditorFormDefaults, HiddenEditorFormFields, useEditorTemplateContract } from '@/deskUiSkeletons/editor/EditorTemplateContract'
import { productDisplayComboValue } from '../../productDisplayCatalog'
import type { MusicCreationIntent, MusicSurfaceKind } from './MusicProviders'

const musicTrackValue = productDisplayComboValue('musictrack')
const musicBoxValue = productDisplayComboValue('musicbox')

function HiddenAction({ name, label }: { name: Parameters<typeof contract>[0]; label: string }) {
  return <button type="button" {...contract(name)} className="hidden" hidden aria-hidden="true" tabIndex={-1}>{label}</button>
}

export function MusicEditorTemplate({ kind, creationIntent, active, editorMode, settingsOpen, onCloseSettings }: { kind: MusicSurfaceKind; creationIntent: MusicCreationIntent; active: boolean; editorMode?: EditorMode; settingsOpen: boolean; onCloseSettings: () => void }) {
  const formModel = useEditorTemplateContract(editorMode)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  const attachCount = editorAttachmentCount(useEditorAttachments(), editorMode)
  const defaultsApplied = useRef('')
  const isCreation = editorMode === 'new_answer' || editorMode === 'new_msg' || editorMode === 'new_submsg'
  const createsTrack = isCreation && creationIntent === 'track'
  const createsBox = isCreation && creationIntent === 'box'
  const productValue = createsTrack ? musicTrackValue : createsBox ? musicBoxValue : 0

  useEffect(() => {
    if (!active || !isCreation || !formModel.rough) { defaultsApplied.current = ''; return }
    const key = `${editorMode}:${creationIntent || 'comment'}:${productValue}`
    if (defaultsApplied.current === key || !formModel.rough) return
    const enabled = dispatchEditorAction({ action: 'set-more-setting', name: 'is_view', value: productValue > 0, reason: 'music:create-product' })
    const selected = productValue > 0
      ? dispatchEditorAction({ action: 'set-more-setting', name: 'is_view_combo', value: productValue, reason: 'music:create-product' })
      : true
    if (enabled && selected) defaultsApplied.current = key
  }, [active, creationIntent, editorMode, formModel.rough, isCreation, productValue])

  const title = createsTrack ? 'Add a music track' : createsBox ? 'Add a music box' : editorMode === 'new_answer' ? 'Add a comment' : editorMode === 'update_msg' ? `Edit ${kind === 'musicbox' ? 'Music Box' : 'Music Track'}` : 'Music editor'
  const editsTrack = kind === 'musictrack' && editorMode === 'update_msg'
  const editsBox = kind === 'musicbox' && editorMode === 'update_msg'
  const attachmentLabel = createsTrack || editsTrack
    ? 'Audio and images'
    : createsBox || editsBox ? 'Images' : 'Attachments'

  return (
    <div {...legacyHookAttrs('editor.root', `min-h-[calc(100vh-5rem)] overflow-hidden rounded-2xl border border-[#d8c4b0] bg-white text-[#2f241d] shadow-sm${active ? '' : ' hidden'}`)} style={{ display: active ? 'flex' : 'none' }} aria-hidden={!active}>
      <div className="flex min-h-0 w-full flex-col p-4 sm:p-6">
        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#6b3f24]">{title}</div>
        <EditorFormDefaults {...formModel} editorMode={editorMode} />
        <HeadShort />
        <button type="button" {...contract('editor.close')} className="hidden" hidden aria-hidden="true" tabIndex={-1}>Close</button>
        <form {...contract('editor.form')} className="flex min-h-0 flex-1 flex-col">
          <EditorHeadFields editorMode={editorMode} />
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <EditorAttachmentsStrip editorMode={editorMode} />
            <button type="button" className="inline-flex h-9 items-center rounded-full border border-[#d8c4b0] bg-white px-3 text-[13px] text-[#6b3f24] hover:bg-[#faf7f2]" onClick={() => setAttachmentsOpen(true)}>{attachmentLabel}{attachCount > 0 ? ` (${attachCount})` : ''}</button>
          </div>
          <EditorBodyFields />
          <HiddenEditorFormFields inputs={formModel.inputs} />
        </form>
        <div {...contract('editor.settings')} className="!hidden" hidden aria-hidden="true" />
        <div {...legacyHookAttrs('editor.actions', 'hidden')} hidden aria-hidden="true">
          <HiddenAction name="editor.action.save" label="Save" /><HiddenAction name="editor.action.draft" label="Draft" /><HiddenAction name="editor.action.delete" label="Delete" /><HiddenAction name="editor.action.clear" label="Clear" /><HiddenAction name="editor.action.saveAndGo" label="Save and go" /><HiddenAction name="editor.action.restore" label="Restore" />
        </div>
        <EditorConfigureModal open={settingsOpen} onClose={onCloseSettings} />
        <EditorAttachmentsModal open={attachmentsOpen} onClose={() => setAttachmentsOpen(false)} editorMode={editorMode} />
      </div>
    </div>
  )
}
